import { useEffect, useState } from "react";
import { TrendingUp, Users, DollarSign, Activity, Search, Gauge, AlertTriangle, PlugZap, CheckCircle2 } from "lucide-react";
import { http } from "@/lib/api";
import { Card } from "@/components/portal/ui";
import { EmailDelivery } from "@/components/admin/EmailDelivery";
import { money } from "@/lib/constants";

const NAMES = { diagnostic: "Diagnostic", blueprint: "Blueprint", guided: "Guided", guided_standalone: "Guided (standalone)", inhome_overnight: "In-Home + Overnight" };
const NC = "NOT CONNECTED";

function Stat({ label, value, source, notConnected }) {
  return (
    <div className="border border-[#E5E1D8] rounded-lg p-4 bg-white">
      <div className="text-xs text-[#8A998C] uppercase tracking-wide">{label}</div>
      {notConnected ? (
        <div className="font-display text-base text-[#9E4F4F] mt-1.5 flex items-center gap-1.5"><PlugZap size={14} /> {NC}</div>
      ) : (
        <div className="font-display text-2xl text-[#1A1918] mt-1">{value}</div>
      )}
      {source && <div className="text-[10px] text-[#A39E98] mt-1">Source: {source}</div>}
    </div>
  );
}
function Section({ icon: Icon, title, children }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 eyebrow text-[#9F634E] mb-4"><Icon size={15} /> {title}</div>
      {children}
    </Card>
  );
}

export default function Health() {
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/admin/health").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-[#6B665E]">Loading…</p>;

  const conn = d.meta?.connections || {};
  const stripeMode = conn.stripe?.mode || "test";

  return (
    <div>
      <div className="eyebrow text-[#8A998C] mb-1">Property Health</div>
      <h1 className="font-display text-3xl text-[#1A1918] mb-1">How healthy is this digital property?</h1>
      <p className="text-[#6B665E] text-sm mb-5">Real production metrics only. Demo/sample and test records are excluded. Traffic & indexing require GA4 / Search Console.</p>

      <EmailDelivery />

      {/* Data source connections */}
      <Card className="p-4 mb-4" data-testid="health-connections">
        <div className="flex flex-wrap gap-2">
          {Object.entries(conn).map(([k, v]) => (
            <span key={k} className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${v.connected ? "border-[#667A66]/40 bg-[#667A66]/10 text-[#4d6b4d]" : "border-[#9E4F4F]/30 bg-[#9E4F4F]/5 text-[#9E4F4F]"}`}>
              {v.connected ? <CheckCircle2 size={12} /> : <PlugZap size={12} />}
              {v.label}: {v.connected ? (v.mode ? `Connected · ${v.mode.toUpperCase()} MODE` : "Connected") : NC}
            </span>
          ))}
        </div>
      </Card>

      {d.meta?.demo_present && (
        <Card className="p-4 mb-6 border-[#C49A5A]/40 bg-[#C49A5A]/10" data-testid="health-demo-banner">
          <div className="flex items-start gap-2 text-sm text-[#8a6a2e]">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold">DEMO DATA present.</span> The database contains {d.meta.demo_ledger_entries} sample order(s) and {d.meta.demo_leads} sample lead(s) used for previewing the product. These are <span className="font-semibold">excluded</span> from every number on this page. Real production metrics currently reflect live customers only.
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-5">
        <Section icon={Users} title="Acquisition">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Total visitors" notConnected source="GA4" />
            <Stat label="Organic" notConnected source="GA4" />
            <Stat label="Paid" notConnected source="GA4 / Google Ads" />
            <Stat label="Lead captures" value={d.acquisition.lead_captures} source="Internal lead records" />
          </div>
          <div className="mt-4 text-sm">
            <div className="text-[#8A998C] text-xs uppercase tracking-wide mb-2">Top landing pages <span className="text-[#A39E98] normal-case">· Source: GA4 / verified attribution</span></div>
            {d.acquisition.top_landing.length === 0 ? <p className="text-[#9E4F4F] text-sm flex items-center gap-1.5"><PlugZap size={14} /> AWAITING DATA — connect GA4 or verified attribution</p> :
              d.acquisition.top_landing.map(([lp, c]) => <div key={lp} className="flex justify-between border-b border-[#F0ECE4] py-1.5"><span className="text-[#1A1918]">{lp}</span><span className="text-[#6B665E]">{c}</span></div>)}
          </div>
        </Section>

        <div className="grid lg:grid-cols-2 gap-5">
          <Section icon={TrendingUp} title="Conversion">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Visitor → purchase" notConnected source="GA4" />
              <Stat label="Checkout completion" value={`${d.conversion.checkout_completion}%`} source="Internal events" />
              <Stat label="Checkout starts" value={d.conversion.checkout_starts} source="Internal events" />
              <Stat label="Purchases" value={d.conversion.purchases} source="Stripe" />
            </div>
            <div className="mt-4 text-sm">
              {Object.keys(d.conversion.product_sales).length === 0 ? <p className="text-[#8A998C]">No real sales yet.</p> :
                Object.entries(d.conversion.product_sales).map(([k, v]) => <div key={k} className="flex justify-between border-b border-[#F0ECE4] py-1.5"><span className="text-[#1A1918]">{NAMES[k] || k}</span><span className="text-[#6B665E]">{v.count} · {money(v.revenue)}</span></div>)}
            </div>
          </Section>
          <Section icon={DollarSign} title="Revenue">
            <p className="text-[10px] text-[#A39E98] mb-2">Source: {d.revenue.source} · Stripe {stripeMode.toUpperCase()} MODE</p>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Total" value={money(d.revenue.total)} />
              <Stat label="Net" value={money(d.revenue.net)} />
              <Stat label="Customers" value={d.revenue.customers} />
              <Stat label="Avg customer value" value={money(d.revenue.avg_customer_value)} />
              <Stat label="Rev / visitor" notConnected source="GA4" />
            </div>
          </Section>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <Section icon={Activity} title="Operations">
            <p className="text-[10px] text-[#A39E98] mb-2">Source: Internal workflow database</p>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Open Diagnostics" value={d.operations.open_diagnostics} />
              <Stat label="Open Blueprints" value={d.operations.open_blueprints} />
              <Stat label="Active Guided" value={d.operations.active_guided} />
              <Stat label="Active Overnight" value={d.operations.active_overnight} />
            </div>
          </Section>
          <Section icon={Gauge} title="Operator Economics">
            <p className="text-[10px] text-[#A39E98] mb-2">Source: Internal + Stripe</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Stat label="Operator hours" value={`${d.economics.operator_hours}h`} />
              <Stat label="Rev / operator hour" value={money(d.economics.rev_per_operator_hour)} />
            </div>
            <div className="text-sm">
              {Object.keys(d.economics.by_product).length === 0 ? <p className="text-[#8A998C]">No real service data yet.</p> :
                Object.entries(d.economics.by_product).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#F0ECE4] py-1.5">
                    <span className="text-[#1A1918]">{NAMES[k] || k}</span>
                    <span className="text-[#6B665E]">{money(v.revenue)} · {v.hours}h · {v.rev_per_hour ? money(v.rev_per_hour) + "/h" : "–"}</span>
                  </div>
                ))}
            </div>
          </Section>
        </div>

        <Section icon={Search} title="SEO Health">
          {d.seo?.search?.connected ? (
            <>
              <p className="text-[10px] text-[#A39E98] mb-2">Source: Google Search Console · last {d.seo.search.period_days || 30}d</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="seo-gsc-stats">
                <Stat label="Search clicks" value={Number(d.seo.search.clicks).toLocaleString()} source="Search Console" />
                <Stat label="Impressions" value={Number(d.seo.search.impressions).toLocaleString()} source="Search Console" />
                <Stat label="Avg CTR" value={`${d.seo.search.ctr}%`} source="Search Console" />
                <Stat label="Avg position" value={d.seo.search.position} source="Search Console" />
              </div>
              <div className="grid md:grid-cols-2 gap-5 mb-4">
                <div>
                  <div className="text-[#8A998C] text-xs uppercase tracking-wide mb-2">Top queries</div>
                  {d.seo.search.top_queries.length === 0 ? <p className="text-[#8A998C] text-sm">No query data yet.</p> :
                    d.seo.search.top_queries.map(([q, clicks, impr, ctr, pos]) => (
                      <div key={q} className="flex justify-between gap-3 border-b border-[#F0ECE4] py-1.5 text-sm">
                        <span className="text-[#1A1918] truncate">{q || "(not set)"}</span>
                        <span className="text-[#6B665E] shrink-0">{clicks} clk · {impr} impr · #{pos}</span>
                      </div>))}
                </div>
                <div>
                  <div className="text-[#8A998C] text-xs uppercase tracking-wide mb-2">Top pages</div>
                  {d.seo.search.top_pages.length === 0 ? <p className="text-[#8A998C] text-sm">No page data yet.</p> :
                    d.seo.search.top_pages.map(([pg, clicks, impr, ctr, pos]) => (
                      <div key={pg} className="flex justify-between gap-3 border-b border-[#F0ECE4] py-1.5 text-sm">
                        <span className="text-[#1A1918] truncate">{pg}</span>
                        <span className="text-[#6B665E] shrink-0">{clicks} clk · {impr} impr · #{pos}</span>
                      </div>))}
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Stat label="Search clicks" notConnected source="Search Console" />
              <Stat label="Impressions" notConnected source="Search Console" />
              <Stat label="Avg CTR" notConnected source="Search Console" />
              <Stat label="Avg position" notConnected source="Search Console" />
            </div>
          )}
          <div className="text-[#8A998C] text-xs uppercase tracking-wide mb-2">Revenue by content cluster <span className="text-[#A39E98] normal-case">· Source: Internal attribution</span></div>
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.keys(d.seo.cluster_revenue).length === 0 ? <p className="text-[#8A998C] text-sm">No real attributed revenue yet.</p> :
              Object.entries(d.seo.cluster_revenue).map(([k, v]) => <Stat key={k} label={k || "direct"} value={money(v)} />)}
          </div>
        </Section>
      </div>
    </div>
  );
}
